import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Search, Plus, Trash2, Command, Blocks, Sparkles, Edit } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.js';
import type { Prompt } from '@/types/prompt.js';
import { CreatePromptModal } from '@/components/prompts/CreatePromptModal.js';
import { LoadingState } from '@/components/common/LoadingState.js';

interface PromptsPanelProps {
  hideHeader?: boolean;
}

export function PromptsPanel({ hideHeader = false }: PromptsPanelProps) {
  const { api } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const promptsData = await api.listPrompts();
      const promptArray = Object.values(promptsData) as Prompt[];
      setPrompts(promptArray);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (promptName: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm(`Are you sure you want to delete the prompt "${promptName}"?`)) {
      return;
    }

    try {
      await api.deleteCustomPrompt(promptName);
      await loadPrompts();
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      alert('Failed to delete prompt. Please try again.');
    }
  };

  const filteredPrompts = prompts.filter((prompt) => {
    const query = searchQuery.toLowerCase();
    return (
      prompt.name.toLowerCase().includes(query) ||
      (prompt.title && prompt.title.toLowerCase().includes(query)) ||
      (prompt.description && prompt.description.toLowerCase().includes(query))
    );
  });

  // Group prompts by source
  const customPrompts = filteredPrompts.filter((p) => p.source === 'custom');
  const configPrompts = filteredPrompts.filter((p) => p.source === 'config');
  const mcpPrompts = filteredPrompts.filter((p) => p.source === 'mcp');

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'mcp':
        return <Blocks className="h-4 w-4 text-blue-600" />;
      case 'config':
        return <Command className="h-4 w-4 text-purple-600" />;
      case 'custom':
        return <Sparkles className="h-4 w-4 text-amber-600" />;
      default:
        return <Command className="h-4 w-4" />;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'mcp':
        return 'MCP';
      case 'config':
        return 'Config';
      case 'custom':
        return 'Custom';
      default:
        return source;
    }
  };

  if (loading) {
    return <LoadingState message="Loading prompts..." />;
  }

  return (
    <>
      <CreatePromptModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={loadPrompts}
      />

      <div className="flex h-full flex-col">
        {/* Header */}
        {!hideHeader && (
          <div className="border-b p-4">
            <h2 className="text-lg font-semibold mb-3">Prompts</h2>

            {/* Search + New Button */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search prompts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button size="icon" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

      {/* Search + New Button (when header is hidden) */}
      {hideHeader && (
        <div className="border-b p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button size="icon" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Prompts List */}
      <div className="flex-1 overflow-y-auto">
        {filteredPrompts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Command className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              {searchQuery ? 'No prompts match your search' : 'No prompts available'}
            </p>
            <p className="text-xs text-muted-foreground/70">
              {searchQuery ? 'Try a different search term' : 'Create a custom prompt to get started'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Custom Prompts */}
            {customPrompts.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Custom ({customPrompts.length})
                </h3>
                <div className="space-y-2">
                  {customPrompts.map((prompt) => (
                    <div
                      key={prompt.name}
                      className="group relative p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getSourceIcon(prompt.source)}
                            <span className="font-mono text-sm font-medium text-foreground">
                              /{prompt.name}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
                              {getSourceLabel(prompt.source)}
                            </span>
                          </div>
                          {(prompt.title || prompt.description) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {prompt.title || prompt.description}
                            </p>
                          )}
                          {prompt.arguments && prompt.arguments.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {prompt.arguments.map((arg) => (
                                <span
                                  key={arg.name}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono"
                                >
                                  {arg.name}
                                  {arg.required && <span className="text-destructive ml-0.5">*</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Open edit modal
                            }}
                          >
                            <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => handleDeletePrompt(prompt.name, e)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Config Prompts */}
            {configPrompts.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Config ({configPrompts.length})
                </h3>
                <div className="space-y-2">
                  {configPrompts.map((prompt) => (
                    <div
                      key={prompt.name}
                      className="p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        {getSourceIcon(prompt.source)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-medium text-foreground">
                              /{prompt.name}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">
                              {getSourceLabel(prompt.source)}
                            </span>
                          </div>
                          {(prompt.title || prompt.description) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {prompt.title || prompt.description}
                            </p>
                          )}
                          {prompt.arguments && prompt.arguments.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {prompt.arguments.map((arg) => (
                                <span
                                  key={arg.name}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono"
                                >
                                  {arg.name}
                                  {arg.required && <span className="text-destructive ml-0.5">*</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MCP Prompts */}
            {mcpPrompts.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  MCP ({mcpPrompts.length})
                </h3>
                <div className="space-y-2">
                  {mcpPrompts.map((prompt) => (
                    <div
                      key={prompt.name}
                      className="p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        {getSourceIcon(prompt.source)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-medium text-foreground">
                              /{prompt.name}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                              {getSourceLabel(prompt.source)}
                            </span>
                          </div>
                          {(prompt.title || prompt.description) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {prompt.title || prompt.description}
                            </p>
                          )}
                          {prompt.arguments && prompt.arguments.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {prompt.arguments.map((arg) => (
                                <span
                                  key={arg.name}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono"
                                >
                                  {arg.name}
                                  {arg.required && <span className="text-destructive ml-0.5">*</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
